import type {
  CreateChecklistFormDTO,
  UpdateChecklistFormDTO,
} from "@/modules/checklist/domain/contracts";
import type { ChecklistResultStatsQueryDTO } from "@/modules/checklist/domain/contracts/result";
import type {
  ChecklistTypeDTO,
  CreateChecklistTypeDTO,
  UpdateChecklistTypeDTO,
} from "@/modules/checklist/domain/contracts/type";
import type { ChecklistTypesListResult } from "@/modules/checklist/domain/repo/type/list-types";
import type {
  ChecklistByIdResult,
  ChecklistsListResult,
  CreateChecklistServiceResult,
  DeleteChecklistServiceResult,
  UpdateChecklistServiceResult,
} from "@/modules/checklist/domain/types";
import type { ChecklistResultStatsResult } from "@/modules/checklist/domain/types/result";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import { fetchLookup, hydrateLookupItem } from "@/shared/lib/utils";

const baseUrl = `${config.apiUrl}/checklists`;

export const checklistsApi = {
  async list(
    query: Partial<OffsetPageQueryDTO> = {},
  ): Promise<ChecklistsListResult> {
    const url = new URL(baseUrl);
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    if (query.offset) url.searchParams.set("offset", String(query.offset));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<ChecklistsListResult>(url.toString());
  },

  async get(id: string): Promise<ChecklistByIdResult> {
    return fetcher.get<ChecklistByIdResult>(`${baseUrl}/${id}`);
  },

  async create(
    input: CreateChecklistFormDTO,
  ): Promise<CreateChecklistServiceResult> {
    return fetcher.post<CreateChecklistServiceResult>(baseUrl, input);
  },

  async update(
    id: string,
    input: UpdateChecklistFormDTO,
  ): Promise<UpdateChecklistServiceResult> {
    return fetcher.put<UpdateChecklistServiceResult>(`${baseUrl}/${id}`, input);
  },

  async remove(id: string): Promise<DeleteChecklistServiceResult> {
    return fetcher.delete<DeleteChecklistServiceResult>(`${baseUrl}/${id}`);
  },

  // Mapping methods
  async getMappings(checklistId: string) {
    const res = await fetch(`${baseUrl}/${checklistId}/mappings`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Failed to fetch mappings: ${res.statusText}`);
    return res.json();
  },

  async createMapping(
    checklistId: string,
    data: {
      sopId?: string | null;
      flowId?: string | null;
      positionId?: string | null;
      departmentId?: string | null;
    },
  ) {
    if (
      !checklistId ||
      checklistId.trim() === "" ||
      checklistId === "undefined"
    ) {
      throw new Error("Invalid checklist ID");
    }
    const res = await fetch(`${baseUrl}/${checklistId}/mappings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to create mapping: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async getMappingEmployees(mappingId: string) {
    const res = await fetch(`${baseUrl}/mappings/${mappingId}/employees`, {
      credentials: "include",
    });
    if (!res.ok)
      throw new Error(`Failed to fetch mapping employees: ${res.statusText}`);
    return res.json();
  },

  async updateMapping(
    mappingId: string,
    data: {
      sopId?: string | null;
      flowId?: string | null;
      positionId?: string | null;
      departmentId?: string | null;
    },
  ) {
    const res = await fetch(`${baseUrl}/mappings/${mappingId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to update mapping: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async deleteMapping(mappingId: string) {
    const res = await fetch(`${baseUrl}/mappings/${mappingId}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to delete mapping: ${res.statusText}`,
      );
    }
    return res.json();
  },

  // KPI mappings (Checklist ↔ KPI — feed KPI from checklist execution)
  async getKpiMappings(checklistId: string): Promise<{
    data: { id: string; checklistId: string; kpiId: string; kpiName: string }[];
  }> {
    const res = await fetch(`${baseUrl}/${checklistId}/kpi-mappings`, {
      credentials: "include",
    });
    if (!res.ok)
      throw new Error(`Failed to fetch KPI mappings: ${res.statusText}`);
    return res.json();
  },

  async createKpiMapping(
    checklistId: string,
    body: { kpiId: string },
  ): Promise<{ id: string }> {
    const res = await fetch(`${baseUrl}/${checklistId}/kpi-mappings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to create KPI mapping: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async deleteKpiMapping(mappingId: string): Promise<void> {
    const res = await fetch(`${baseUrl}/kpi-mappings/${mappingId}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to delete KPI mapping: ${res.statusText}`,
      );
    }
  },

  async listExecutionHistory(params: {
    limit?: number;
    offset?: number;
    checklistId?: string;
    departmentId?: string;
    userId?: string;
  }): Promise<{
    data: Array<{
      id: string;
      checklistId: string;
      checklistTitle: string;
      departmentId: string | null;
      departmentName: string | null;
      userId: string;
      userName: string;
      userEmail: string;
      status: "in_progress" | "completed" | "cancelled";
      startedAt: string;
      completedAt: string | null;
      createdAt: string;
    }>;
    meta: { total: number; limit: number; offset: number };
  }> {
    const url = new URL(`${baseUrl}/executions/history`);
    url.searchParams.set("limit", String(params.limit ?? 20));
    url.searchParams.set("offset", String(params.offset ?? 0));
    if (params.checklistId)
      url.searchParams.set("checklistId", params.checklistId);
    if (params.departmentId)
      url.searchParams.set("departmentId", params.departmentId);
    if (params.userId) url.searchParams.set("userId", params.userId);
    return fetcher.get(url.toString());
  },

  async getExecutionStats(
    query: ChecklistResultStatsQueryDTO,
  ): Promise<ChecklistResultStatsResult> {
    const url = new URL(`${baseUrl}/executions/stats`);
    if (query.departmentId)
      url.searchParams.set("departmentId", query.departmentId);
    if (query.checklistId)
      url.searchParams.set("checklistId", query.checklistId);
    if (query.startDate) url.searchParams.set("startDate", query.startDate);
    if (query.endDate) url.searchParams.set("endDate", query.endDate);
    return fetcher.get<ChecklistResultStatsResult>(url.toString());
  },

  async getExecutionDetail(executionId: string): Promise<{
    data: {
      id: string;
      checklistId: string;
      checklistTitle: string;
      departmentId: string | null;
      departmentName: string | null;
      userId: string;
      userName: string;
      userEmail: string;
      status: "in_progress" | "completed" | "cancelled";
      notes: string | null;
      startedAt: string;
      completedAt: string | null;
      createdAt: string;
      updatedAt: string;
      items: Array<{
        id: string;
        itemId: string;
        order: number;
        title: string;
        description: string | null;
        isRequired: boolean;
        allowNotes: boolean;
        formId: string | null;
        formName: string | null;
        isCompleted: boolean;
        notes: string | null;
        photoUrl: string | null;
        completedAt: string | null;
      }>;
    };
  }> {
    return fetcher.get(`${baseUrl}/executions/${executionId}`);
  },

  /** List checklists that feed this KPI (for KPI detail page). */
  async getChecklistMappingsByKpi(kpiId: string): Promise<{
    data: {
      id: string;
      checklistId: string;
      checklistTitle: string;
      kpiId: string;
    }[];
  }> {
    const res = await fetch(
      `${baseUrl}/kpi-mappings?${new URLSearchParams({ kpiId })}`,
      { credentials: "include" },
    );
    if (!res.ok)
      throw new Error(
        `Failed to fetch checklist mappings for KPI: ${res.statusText}`,
      );
    return res.json();
  },
};

export const checklistTypesApi = {
  async list(query: OffsetPageQueryDTO): Promise<ChecklistTypesListResult> {
    const url = new URL(`${baseUrl}/types`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<ChecklistTypesListResult>(url.toString());
  },

  async get(id: string) {
    return fetcher.get(`${baseUrl}/types/${id}`);
  },

  async create(input: CreateChecklistTypeDTO) {
    return fetcher.post<{ value: ChecklistTypeDTO }>(`${baseUrl}/types`, input);
  },

  async update(id: string, input: UpdateChecklistTypeDTO) {
    return fetcher.patch<{ value: ChecklistTypeDTO }>(
      `${baseUrl}/types/${id}`,
      input,
    );
  },

  async delete(id: string) {
    return fetcher.delete<{ value: { ok: true } }>(`${baseUrl}/types/${id}`);
  },

  async lookup(params: {
    query: string;
    cursor?: string | null;
    pageSize: number;
  }) {
    return fetchLookup(`${baseUrl}/types/lookup`, params);
  },

  async hydrate(id: string) {
    return hydrateLookupItem(`${baseUrl}/types/lookup`, id);
  },
};
